import type { LabelSizeId } from "../types";
import { isLabelSizeId } from "./labelPrintingSettings";
import { DEFAULT_LABEL_SIZE } from "../zpl/sizes";
import { migrateLegacyPrinterSource, type PrinterSource } from "./printerSelection";

export const WORKSTATION_STORAGE_KEY = "fixid.labelPrinting.workstation";
/** Legacy key used before the settings panel. */
export const LEGACY_WORKSTATION_STORAGE_KEY = "fixid.zebraPrinter";

export interface WorkstationLabelPrintingSettings {
  printerName: string | null;
  /** How printerName was chosen; manual choices are never overwritten by auto-pick. */
  printerSource: PrinterSource | null;
  /** Non-Zebra printer names the user confirmed printing to. */
  confirmedNonZebraPrinters: string[];
  /** When set, overrides company default label size for this PC. */
  labelSizeOverride: LabelSizeId | null;
  lastTestAt: string | null;
}

export const DEFAULT_WORKSTATION_LABEL_PRINTING: WorkstationLabelPrintingSettings = {
  printerName: null,
  printerSource: null,
  confirmedNonZebraPrinters: [],
  labelSizeOverride: null,
  lastTestAt: null,
};

function safeGet(key: string): string | null {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    if (typeof window !== "undefined") window.localStorage.setItem(key, value);
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

function safeRemove(key: string): void {
  try {
    if (typeof window !== "undefined") window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function legacyDefaults(): WorkstationLabelPrintingSettings {
  const m = migrateLegacyPrinterSource(safeGet(LEGACY_WORKSTATION_STORAGE_KEY), null);
  return { ...DEFAULT_WORKSTATION_LABEL_PRINTING, ...m };
}

/**
 * Read workstation settings. Migrates Lovable-era `{ printer }` and the
 * legacy `fixid.zebraPrinter` key into `{ printerName }`. Legacy names without
 * a source are kept only if they look like a Zebra printer.
 */
export function getWorkstationLabelPrintingSettings(): WorkstationLabelPrintingSettings {
  const raw = safeGet(WORKSTATION_STORAGE_KEY);
  if (!raw) return legacyDefaults();
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const fromPrinterName =
      typeof parsed.printerName === "string" && parsed.printerName ? parsed.printerName : null;
    const fromPrinter =
      typeof parsed.printer === "string" && parsed.printer ? parsed.printer : null;
    const name = fromPrinterName ?? fromPrinter ?? safeGet(LEGACY_WORKSTATION_STORAGE_KEY);
    const m = migrateLegacyPrinterSource(name, parsed.printerSource);
    return {
      ...m,
      confirmedNonZebraPrinters: Array.isArray(parsed.confirmedNonZebraPrinters)
        ? parsed.confirmedNonZebraPrinters.filter((x): x is string => typeof x === "string")
        : [],
      labelSizeOverride: isLabelSizeId(parsed.labelSizeOverride) ? parsed.labelSizeOverride : null,
      lastTestAt: typeof parsed.lastTestAt === "string" ? parsed.lastTestAt : null,
    };
  } catch {
    return legacyDefaults();
  }
}

export function setWorkstationLabelPrintingSettings(
  next: Partial<WorkstationLabelPrintingSettings>,
): WorkstationLabelPrintingSettings {
  const current = getWorkstationLabelPrintingSettings();
  const merged: WorkstationLabelPrintingSettings = { ...current };
  (Object.keys(next) as (keyof WorkstationLabelPrintingSettings)[]).forEach((k) => {
    if (next[k] !== undefined) (merged as any)[k] = next[k];
  });
  if (!merged.printerName) merged.printerSource = null;
  safeSet(WORKSTATION_STORAGE_KEY, JSON.stringify(merged));
  if (merged.printerName) {
    safeSet(LEGACY_WORKSTATION_STORAGE_KEY, merged.printerName);
  } else {
    safeRemove(LEGACY_WORKSTATION_STORAGE_KEY);
  }
  return merged;
}

/** @deprecated Prefer `getWorkstationLabelPrintingSettings`. */
export function readWorkstationSettings(): WorkstationLabelPrintingSettings {
  return getWorkstationLabelPrintingSettings();
}

/** @deprecated Prefer `setWorkstationLabelPrintingSettings`. */
export function writeWorkstationSettings(next: WorkstationLabelPrintingSettings): void {
  setWorkstationLabelPrintingSettings(next);
}

/**
 * Resolve the effective spare-part label size:
 * workstation override -> company default -> DEFAULT_LABEL_SIZE (76x51).
 */
export function resolveSparePartLabelSize(companyDefault?: LabelSizeId): LabelSizeId {
  const ws = getWorkstationLabelPrintingSettings();
  return ws.labelSizeOverride ?? companyDefault ?? DEFAULT_LABEL_SIZE;
}
